export interface ChromaticityPoint {
  readonly x: number;
  readonly y: number;
}

export interface UvPoint {
  readonly u: number;
  readonly v: number;
}

export type Triangle = readonly [ChromaticityPoint, ChromaticityPoint, ChromaticityPoint];
export type UvTriangle = readonly [UvPoint, UvPoint, UvPoint];

export type ColorSpaceName = 'sRGB' | 'AdobeRGB' | 'BT2020' | 'DCI-P3' | 'NTSC1953';

export interface ColorSpaceDefinition {
  readonly name: ColorSpaceName;
  readonly label: string;
  readonly red: ChromaticityPoint;
  readonly green: ChromaticityPoint;
  readonly blue: ChromaticityPoint;
  readonly white: ChromaticityPoint;
}

export interface GamutTriangleInput {
  readonly red: ChromaticityPoint;
  readonly green: ChromaticityPoint;
  readonly blue: ChromaticityPoint;
}

export interface ColorSpaceCoverageResult {
  readonly colorSpace: ColorSpaceDefinition;
  readonly cie1931CoveragePercent: number;
  readonly cie1976CoveragePercent: number;
  readonly cie1931AreaRatioPercent: number;
  readonly cie1976AreaRatioPercent: number;
  readonly cie1931Intersection: readonly ChromaticityPoint[];
  readonly cie1976Intersection: readonly UvPoint[];
}

export interface ContrastResult {
  readonly whiteLuminanceCdM2: number;
  readonly blackLuminanceCdM2: number;
  readonly ratio: number | null;
  readonly label: string;
}

export interface UniformityResult {
  readonly values: readonly number[];
  readonly min: number;
  readonly max: number;
  readonly average: number;
  readonly uniformityPercent: number;
  readonly worstIndex: number;
}

const D65: ChromaticityPoint = { x: 0.3127, y: 0.329 };

export const COLOR_SPACES: readonly ColorSpaceDefinition[] = [
  {
    name: 'sRGB',
    label: 'sRGB / Rec.709',
    red: { x: 0.64, y: 0.33 },
    green: { x: 0.3, y: 0.6 },
    blue: { x: 0.15, y: 0.06 },
    white: D65,
  },
  {
    name: 'AdobeRGB',
    label: 'Adobe RGB (1998)',
    red: { x: 0.64, y: 0.33 },
    green: { x: 0.21, y: 0.71 },
    blue: { x: 0.15, y: 0.06 },
    white: D65,
  },
  {
    name: 'BT2020',
    label: 'BT.2020 / Rec.2020',
    red: { x: 0.708, y: 0.292 },
    green: { x: 0.17, y: 0.797 },
    blue: { x: 0.131, y: 0.046 },
    white: D65,
  },
  {
    name: 'DCI-P3',
    label: 'DCI-P3 (Display P3 D65)',
    red: { x: 0.68, y: 0.32 },
    green: { x: 0.265, y: 0.69 },
    blue: { x: 0.15, y: 0.06 },
    white: D65,
  },
  {
    name: 'NTSC1953',
    label: 'NTSC 1953',
    red: { x: 0.67, y: 0.33 },
    green: { x: 0.21, y: 0.71 },
    blue: { x: 0.14, y: 0.08 },
    white: { x: 0.3101, y: 0.3162 },
  },
];

export function getColorSpace(name: ColorSpaceName): ColorSpaceDefinition {
  const found = COLOR_SPACES.find((space) => space.name === name);
  if (!found) {
    throw new Error(`Unknown color space: ${name}`);
  }
  return found;
}

function toTriangle(input: GamutTriangleInput): Triangle {
  return [input.red, input.green, input.blue];
}

export function colorSpaceTriangle(space: ColorSpaceDefinition): Triangle {
  return [space.red, space.green, space.blue];
}

function xyToUv(point: ChromaticityPoint): UvPoint {
  const denominator = -2 * point.x + 12 * point.y + 3;
  if (Math.abs(denominator) < Number.EPSILON) {
    return { u: Number.NaN, v: Number.NaN };
  }

  return {
    u: (4 * point.x) / denominator,
    v: (9 * point.y) / denominator,
  };
}

export function calculateGamutCoverage(
  input: GamutTriangleInput,
  colorSpaces: readonly ColorSpaceDefinition[] = COLOR_SPACES,
): readonly ColorSpaceCoverageResult[] {
  const sampleXy = toTriangle(input);
  const sampleUv = mapTriangle(sampleXy, xyToUv);
  const sampleXyArea = polygonArea(sampleXy);
  const sampleUvArea = polygonAreaUv(sampleUv);

  return colorSpaces.map((colorSpace) => {
    const referenceXy = colorSpaceTriangle(colorSpace);
    const referenceUv = mapTriangle(referenceXy, xyToUv);
    const xyIntersection = intersectConvexPolygons(sampleXy, referenceXy);
    const uvIntersection = intersectConvexUvPolygons(sampleUv, referenceUv);
    const referenceXyArea = polygonArea(referenceXy);
    const referenceUvArea = polygonAreaUv(referenceUv);

    return {
      colorSpace,
      cie1931CoveragePercent: safePercent(polygonArea(xyIntersection), referenceXyArea),
      cie1976CoveragePercent: safePercent(polygonAreaUv(uvIntersection), referenceUvArea),
      cie1931AreaRatioPercent: safePercent(sampleXyArea, referenceXyArea),
      cie1976AreaRatioPercent: safePercent(sampleUvArea, referenceUvArea),
      cie1931Intersection: xyIntersection,
      cie1976Intersection: uvIntersection,
    };
  });
}

export function calculateContrast(
  whiteLuminanceCdM2: number,
  blackLuminanceCdM2: number,
): ContrastResult {
  if (!Number.isFinite(whiteLuminanceCdM2) || !Number.isFinite(blackLuminanceCdM2)) {
    throw new Error('Luminance values must be finite numbers.');
  }

  if (blackLuminanceCdM2 <= 0) {
    return {
      whiteLuminanceCdM2,
      blackLuminanceCdM2,
      ratio: null,
      label: '측정 한계 초과',
    };
  }

  const ratio = whiteLuminanceCdM2 / blackLuminanceCdM2;
  return {
    whiteLuminanceCdM2,
    blackLuminanceCdM2,
    ratio,
    label: `${Math.round(ratio).toLocaleString('ko-KR')}:1`,
  };
}

export function formatPercent(value: number, digits = 1): string {
  if (!Number.isFinite(value)) {
    return '-';
  }
  return `${value.toFixed(digits)} %`;
}

function safePercent(numerator: number, denominator: number): number {
  if (!Number.isFinite(numerator) || !Number.isFinite(denominator) || denominator <= 0) {
    return 0;
  }
  return (numerator / denominator) * 100;
}

function mapTriangle<T>(
  triangle: Triangle,
  mapper: (point: ChromaticityPoint) => T,
): readonly [T, T, T] {
  return [mapper(triangle[0]), mapper(triangle[1]), mapper(triangle[2])];
}

function polygonArea(points: readonly ChromaticityPoint[]): number {
  if (points.length < 3) {
    return 0;
  }

  let sum = 0;
  for (let index = 0; index < points.length; index += 1) {
    const current = chromaticityAt(points, index);
    const next = chromaticityAt(points, index + 1);
    sum += current.x * next.y - next.x * current.y;
  }

  return Math.abs(sum) / 2;
}

function polygonAreaUv(points: readonly UvPoint[]): number {
  if (points.length < 3) {
    return 0;
  }

  let sum = 0;
  for (let index = 0; index < points.length; index += 1) {
    const current = uvAt(points, index);
    const next = uvAt(points, index + 1);
    sum += current.u * next.v - next.u * current.v;
  }

  return Math.abs(sum) / 2;
}

function signedArea(points: readonly ChromaticityPoint[]): number {
  let sum = 0;
  for (let index = 0; index < points.length; index += 1) {
    const current = chromaticityAt(points, index);
    const next = chromaticityAt(points, index + 1);
    sum += current.x * next.y - next.x * current.y;
  }
  return sum / 2;
}

function signedAreaUv(points: readonly UvPoint[]): number {
  let sum = 0;
  for (let index = 0; index < points.length; index += 1) {
    const current = uvAt(points, index);
    const next = uvAt(points, index + 1);
    sum += current.u * next.v - next.u * current.v;
  }
  return sum / 2;
}

function chromaticityAt(points: readonly ChromaticityPoint[], index: number): ChromaticityPoint {
  if (points.length === 0) {
    throw new Error('Chromaticity polygon is empty.');
  }

  const normalizedIndex = ((index % points.length) + points.length) % points.length;
  const point = points[normalizedIndex];
  if (!point) {
    throw new Error(`Missing chromaticity point at index ${normalizedIndex}.`);
  }
  return point;
}

function uvAt(points: readonly UvPoint[], index: number): UvPoint {
  if (points.length === 0) {
    throw new Error("u'v' polygon is empty.");
  }

  const normalizedIndex = ((index % points.length) + points.length) % points.length;
  const point = points[normalizedIndex];
  if (!point) {
    throw new Error(`Missing u'v' point at index ${normalizedIndex}.`);
  }
  return point;
}

function intersectConvexPolygons(
  subjectPolygon: readonly ChromaticityPoint[],
  clipPolygon: readonly ChromaticityPoint[],
): readonly ChromaticityPoint[] {
  const clipPoints = signedArea(clipPolygon) >= 0 ? [...clipPolygon] : [...clipPolygon].reverse();
  let output = [...subjectPolygon];

  for (let index = 0; index < clipPoints.length; index += 1) {
    const clipStart = chromaticityAt(clipPoints, index);
    const clipEnd = chromaticityAt(clipPoints, index + 1);
    const input = output;
    output = [];

    if (input.length === 0) {
      break;
    }

    let previous = chromaticityAt(input, input.length - 1);
    for (const current of input) {
      const currentInside = isInside(current, clipStart, clipEnd);
      const previousInside = isInside(previous, clipStart, clipEnd);

      if (currentInside) {
        if (!previousInside) {
          output.push(lineIntersection(previous, current, clipStart, clipEnd));
        }
        output.push(current);
      } else if (previousInside) {
        output.push(lineIntersection(previous, current, clipStart, clipEnd));
      }

      previous = current;
    }
  }

  return output;
}

function intersectConvexUvPolygons(
  subjectPolygon: readonly UvPoint[],
  clipPolygon: readonly UvPoint[],
): readonly UvPoint[] {
  const clipPoints = signedAreaUv(clipPolygon) >= 0 ? [...clipPolygon] : [...clipPolygon].reverse();
  let output = [...subjectPolygon];

  for (let index = 0; index < clipPoints.length; index += 1) {
    const clipStart = uvAt(clipPoints, index);
    const clipEnd = uvAt(clipPoints, index + 1);
    const input = output;
    output = [];

    if (input.length === 0) {
      break;
    }

    let previous = uvAt(input, input.length - 1);
    for (const current of input) {
      const currentInside = isInsideUv(current, clipStart, clipEnd);
      const previousInside = isInsideUv(previous, clipStart, clipEnd);

      if (currentInside) {
        if (!previousInside) {
          output.push(lineIntersectionUv(previous, current, clipStart, clipEnd));
        }
        output.push(current);
      } else if (previousInside) {
        output.push(lineIntersectionUv(previous, current, clipStart, clipEnd));
      }

      previous = current;
    }
  }

  return output;
}

function isInside(
  point: ChromaticityPoint,
  edgeStart: ChromaticityPoint,
  edgeEnd: ChromaticityPoint,
): boolean {
  return (
    (edgeEnd.x - edgeStart.x) * (point.y - edgeStart.y) -
      (edgeEnd.y - edgeStart.y) * (point.x - edgeStart.x) >=
    -1e-12
  );
}

function isInsideUv(point: UvPoint, edgeStart: UvPoint, edgeEnd: UvPoint): boolean {
  return (
    (edgeEnd.u - edgeStart.u) * (point.v - edgeStart.v) -
      (edgeEnd.v - edgeStart.v) * (point.u - edgeStart.u) >=
    -1e-12
  );
}

function lineIntersection(
  aStart: ChromaticityPoint,
  aEnd: ChromaticityPoint,
  bStart: ChromaticityPoint,
  bEnd: ChromaticityPoint,
): ChromaticityPoint {
  const denominator =
    (aStart.x - aEnd.x) * (bStart.y - bEnd.y) - (aStart.y - aEnd.y) * (bStart.x - bEnd.x);

  if (Math.abs(denominator) < 1e-12) {
    return aEnd;
  }

  const aCross = aStart.x * aEnd.y - aStart.y * aEnd.x;
  const bCross = bStart.x * bEnd.y - bStart.y * bEnd.x;

  return {
    x: (aCross * (bStart.x - bEnd.x) - (aStart.x - aEnd.x) * bCross) / denominator,
    y: (aCross * (bStart.y - bEnd.y) - (aStart.y - aEnd.y) * bCross) / denominator,
  };
}

function lineIntersectionUv(
  aStart: UvPoint,
  aEnd: UvPoint,
  bStart: UvPoint,
  bEnd: UvPoint,
): UvPoint {
  const denominator =
    (aStart.u - aEnd.u) * (bStart.v - bEnd.v) - (aStart.v - aEnd.v) * (bStart.u - bEnd.u);

  if (Math.abs(denominator) < 1e-12) {
    return aEnd;
  }

  const aCross = aStart.u * aEnd.v - aStart.v * aEnd.u;
  const bCross = bStart.u * bEnd.v - bStart.v * bEnd.u;

  return {
    u: (aCross * (bStart.u - bEnd.u) - (aStart.u - aEnd.u) * bCross) / denominator,
    v: (aCross * (bStart.v - bEnd.v) - (aStart.v - aEnd.v) * bCross) / denominator,
  };
}
